import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Typography } from "@mui/material";
import apiURL from "../../services/apiURL";
import useToken from "../useToken";
import { checkSessionExpiration } from "../../services/checkSessionExpiration";
import { IDashboardRow } from "../../services/API/GetDashboardData";
import { IExpenseDashboardSummary } from "../../services/API/GetExpenseDashboardSummary";

export interface CachedMetric<T> {
  status: "fresh" | "stale" | "unavailable";
  calculatedAtUtc: string | null;
  data: T | null;
}
interface UnderwritingSummary {
  timeZone: string;
  trends: Record<string, Array<{ label: string; value: number }>>;
  marketers: Record<string, Array<{ name: string; count: number }>>;
  underwriters: Record<string, Array<{ name: string; count: number }>>;
}
interface MetricData {
  dashboardData: IDashboardRow;
  weeklySales: Array<{ regDate: string; salesCount: number; loginCount: number }>;
  motor: Array<{ name: string; value: number }>;
  fambul: Array<{ name: string; value: number }>;
  recentPayments: Array<{ paymentDate: string; premiumAmount: number }>;
  underwriting: UnderwritingSummary;
}
type Summary = { [K in keyof MetricData]?: CachedMetric<MetricData[K]> };
const CacheContext = createContext<{ summary: Summary; loading: boolean }>({ summary: {}, loading: true });

function handleCacheAuthFailure(error: unknown) {
  if (axios.isAxiosError(error) && error.response?.status === 401) {
    sessionStorage.clear();
    window.location.href = "/#/login";
  }
}

export const DashboardCacheProvider: React.FC = ({ children }) => {
  const { token } = useToken();
  const [state, setState] = useState<{ token: string; summary: Summary; loading: boolean }>({ token, summary: {}, loading: true });
  useEffect(() => {
    if (!checkSessionExpiration()) return;
    const controller = new AbortController();
    setState({ token, summary: {}, loading: true });
    axios.get<Summary>(`${apiURL()}dashboard-cache/summary`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    }).then(response => {
      if (!controller.signal.aborted) setState({ token, summary: response.data, loading: false });
    }).catch(error => {
      if (!controller.signal.aborted) {
        handleCacheAuthFailure(error);
        setState({ token, summary: {}, loading: false });
      }
    });
    return () => controller.abort();
  }, [token]);
  return <CacheContext.Provider value={state.token === token ? state : { summary: {}, loading: true }}>{children}</CacheContext.Provider>;
};

export function useDashboardMetric<K extends keyof MetricData>(key: K) {
  const { summary, loading } = useContext(CacheContext);
  const metric = summary[key] as CachedMetric<MetricData[K]> | undefined;
  return { metric, data: metric?.data, loading };
}

export function CacheStatus({ metric, loading = false }: { metric?: CachedMetric<unknown>; loading?: boolean }) {
  if (!loading && metric?.data && metric.status === "fresh") return null;
  return <Typography variant="caption" color="text.secondary" display="block">
    {loading ? "Loading snapshot…" : !metric?.data ? "Snapshot unavailable" :
      "Cached data is stale."}
  </Typography>;
}

export const getCachedExpenses = async (token: string, signal: AbortSignal) => {
  if (!checkSessionExpiration()) throw new Error("Session expired");
  return axios.get<CachedMetric<IExpenseDashboardSummary>>(`${apiURL()}dashboard-cache/expenses`, {
    headers: { Authorization: `Bearer ${token}` }, signal,
  }).then(response => response.data).catch(error => {
    if (!signal.aborted) handleCacheAuthFailure(error);
    throw error;
  });
};
